// src/pages/Admin/ReglementInscription/ReglementInscriptionPage.jsx

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaEuroSign,
  FaFileExcel,
  FaPlus,
  FaRedo,
  FaUsers,
  FaCheckCircle,
  FaClock,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";

import { deleteReglementInscription } from "../../../../services/reglementinscriptionServices";

import useReglementsInscriptionAdmin from "./useReglementsInscriptionAdmin";

import {
  calculateReglementTotal,
  fmtEUR,
  getRoleId,
  getStoredUserInfo,
} from "./reglement.helpers";

import ReglementFiltersBar from "./components/ReglementFiltersBar";
import ReglementsTable from "./components/ReglementsTable";
import CreateReglementInscriptionDrawer from "./components/CreateReglementInscriptionDrawer";
import EditReglementInscriptionDrawer from "./components/EditReglementInscriptionDrawer";

function MiniStat({ icon, label, value, helper, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    rose: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
            {label}
          </p>

          <p className="mt-0.5 text-xl font-bold text-slate-950 dark:text-white">
            {typeof value === "number" ? value.toLocaleString("fr-FR") : value}
          </p>

          {helper ? (
            <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
              {helper}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Règlements inscriptions
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ReglementInscriptionPage() {
  useScrollToTop();

  const navigate = useNavigate();
  const { user } = useUser();

  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getRoleId(user || storedUser);

  const { state, setters, handlers } = useReglementsInscriptionAdmin();

  const {
    loading,
    clubSettings,
    seasons,
    groups,
    searchTerm,
    selectedSeason,
    passSportFilter,
    statusFilters,
    selectedGroup,
    passSportOptions,
    sortField,
    sortOrder,
    editingStatusId,
    statusDraft,
    savingStatusId,
    visibleCount,
    scrollRef,
    filtered,
    visibleReglements,
    totalAffiche,
    stats,
  } = state;

  const {
    setSearchTerm,
    setSelectedSeason,
    setPassSportFilter,
    setStatusFilters,
    setSelectedGroup,
    setEditingStatusId,
    setStatusDraft,
  } = setters;

  const {
    fetchReglements,
    fetchInitialData,
    saveStatus,
    syncDossierValidateReglement,
    toggleNameSort,
    toggleTotalSort,
    resetFilters,
    onScroll,
  } = handlers;

  const [drawer, setDrawer] = useState({
    type: null,
    reglement: null,
  });

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce règlement ?")) return;

    try {
      await deleteReglementInscription(id);
      toast.success("Règlement supprimé.");
      await fetchReglements();
    } catch (error) {
      console.error("Erreur suppression règlement :", error);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const handleOpenAttestation = (reglement) => {
    const adherent = reglement?.Adherent || {};
    const settings = clubSettings || {};

    navigate("/attestation", {
      state: {
        prenom: adherent.prenom || "",
        nom: adherent.nom || "",
        dateNaissance: adherent.date_naissance
          ? new Date(adherent.date_naissance).toLocaleDateString("fr-FR")
          : "",
        adresse: adherent.adresse || "",
        cp: adherent.cp || "",
        commune: adherent.commune || "",
        cotisation: Number(reglement?.montant_cotisation) || 0,
        licence: Number(reglement?.montant_lic_assurance) || 0,
        reductionFoyer: Number(reglement?.reduction_foyer) || 0,
        passSport: Number(reglement?.pass_sport) || 0,
        reductionSaison: Number(reglement?.reduction_saison) || 0,
        typeReglement: reglement?.type_reglement || "",
        nbReglement: Number(reglement?.nb_reglement) || 0,
        statutReglement: reglement?.statut_reglement || "",
        observations: reglement?.observations || "",
        datePaiement: new Date().toLocaleDateString("fr-FR"),
        president: settings.responsable_club || "",
        association: settings.nom_club || "",
        logo: settings.logo_club || "",
        adresse_club: settings.adresse_club || "",
        tel_club: settings.tel_club || "",
        mail_club: settings.mail_club || "",
        siteweb_club: settings.siteweb_club || "",
        siret_club: settings.siret_club || "",
        saison_active: settings.saison_active || selectedSeason,
      },
    });
  };

  const exportExcel = () => {
    const rows = filtered.map((reglement) => {
      const adherent = reglement?.Adherent || {};
      const groups = (adherent?.Groups || [])
        .map((group) => group?.nom)
        .join(", ");

      return {
        Nom: adherent?.nom || "",
        Prénom: adherent?.prenom || "",
        Email: adherent?.email || "",
        Saison: reglement?.saison || "",
        Statut: reglement?.statut_reglement || "",
        Type: reglement?.type_reglement || "",
        "Nombre de règlements": reglement?.nb_reglement ?? "",
        "Licence + assurance": Number(reglement?.montant_lic_assurance) || 0,
        Cotisation: Number(reglement?.montant_cotisation) || 0,
        "Réduction foyer": Number(reglement?.reduction_foyer) || 0,
        "Pass'Sport": Number(reglement?.pass_sport) || 0,
        "Réduction saison": Number(reglement?.reduction_saison) || 0,
        Total: calculateReglementTotal(reglement),
        Groupes: groups,
        Observations: reglement?.observations || "",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Reglements");

    const buffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `reglements_inscription_${selectedSeason || "toutes_saisons"}.xlsx`,
    );
  };

  if (roleId !== 1) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <div className="relative z-40 overflow-visible rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Administration
                </p>

                <h1 className="mt-1 text-2xl font-bold text-slate-900 sm:text-[30px] dark:text-white">
                  Règlements inscriptions
                </h1>

                <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Suivez les licences, cotisations, réductions, Pass'Sport,
                  paiements et attestations d'inscription.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setDrawer({ type: "create", reglement: null })}
                  className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700"
                >
                  <FaPlus />
                  Créer
                </button>

                <button
                  type="button"
                  onClick={exportExcel}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaFileExcel />
                  Excel
                </button>

                <button
                  type="button"
                  onClick={fetchInitialData}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  <FaRedo className={loading ? "animate-spin" : ""} />
                  Actualiser
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MiniStat
            icon={<FaUsers />}
            label="Affichés"
            value={stats.affiches}
            helper={`${stats.total} règlement(s) au total`}
            accent="indigo"
          />

          <MiniStat
            icon={<FaCheckCircle />}
            label="Réglés"
            value={stats.regles}
            helper="Statut réglé"
            accent="emerald"
          />

          <MiniStat
            icon={<FaClock />}
            label="En cours"
            value={stats.enCours}
            helper="En cours ou partiel"
            accent="amber"
          />

          <MiniStat
            icon={<FaEuroSign />}
            label="Total filtré"
            value={fmtEUR(totalAffiche)}
            helper="Montant affiché"
            accent="rose"
          />
        </div>

        <div className="relative z-10 overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <ReglementFiltersBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedSeason={selectedSeason}
            setSelectedSeason={setSelectedSeason}
            seasons={seasons}
            passSportFilter={passSportFilter}
            setPassSportFilter={setPassSportFilter}
            passSportOptions={passSportOptions}
            statusFilters={statusFilters}
            setStatusFilters={setStatusFilters}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            groups={groups}
            filteredCount={filtered.length}
            totalAffiche={totalAffiche}
            resetFilters={resetFilters}
          />

          <ReglementsTable
            rows={visibleReglements}
            totalCount={filtered.length}
            visibleCount={visibleCount}
            loading={loading}
            scrollRef={scrollRef}
            onScroll={onScroll}
            sortField={sortField}
            sortOrder={sortOrder}
            toggleNameSort={toggleNameSort}
            toggleTotalSort={toggleTotalSort}
            editingStatusId={editingStatusId}
            setEditingStatusId={setEditingStatusId}
            statusDraft={statusDraft}
            setStatusDraft={setStatusDraft}
            savingStatusId={savingStatusId}
            saveStatus={saveStatus}
            onEdit={(reglement) => setDrawer({ type: "edit", reglement })}
            onDelete={handleDelete}
            onAttestation={handleOpenAttestation}
            onOnlinePayment={() => navigate("/paiements")}
            clubSettings={clubSettings}
          />
        </div>
      </div>

      <CreateReglementInscriptionDrawer
        isOpen={drawer.type === "create"}
        onClose={() => setDrawer({ type: null, reglement: null })}
        refreshList={fetchReglements}
        syncDossierValidateReglement={syncDossierValidateReglement}
      />

      <EditReglementInscriptionDrawer
        isOpen={drawer.type === "edit"}
        onClose={() => setDrawer({ type: null, reglement: null })}
        reglement={drawer.reglement}
        refreshList={fetchReglements}
        syncDossierValidateReglement={syncDossierValidateReglement}
      />

      <ToastContainer />
    </section>
  );
}
